import { useState } from "react";
import { PhoneIcon, XMarkIcon } from "@heroicons/react/24/outline";

// Phone number validation utility
const isValidPhoneNumber = (phone) => {
  const phoneRegex = /^[+]?[1-9][\d]{0,15}$/
  return phoneRegex.test(phone.replace(/\s|-|\(|\)/g, ''))
}

// Lead Chips Input Component (non-editable, backend handles phone numbers)
const LeadChipsInput = ({ label, leads, onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState("")
  const [inputError, setInputError] = useState("")

  const addLead = (phoneNumber) => {
    const trimmedPhone = phoneNumber.trim()
    if (!trimmedPhone) return

    if (!isValidPhoneNumber(trimmedPhone)) {
      setInputError("Please enter a valid phone number")
      return
    }

    if (leads.some(l => l.number === trimmedPhone)) {
      setInputError("Phone number already added")
      return
    }

    onChange([...leads, {
      number: trimmedPhone,
      name: `Contact ${leads.length + 1}`,
      id: `temp_${Date.now()}_${leads.length}`,
      hasPhone: true
    }])
    setInputValue("")
    setInputError("")
  }

  const removeLead = (index) => {
    onChange(leads.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addLead(inputValue)
    } else if (e.key === 'Backspace' && !inputValue && leads.length > 0) {
      // Remove last lead when backspace is pressed on empty input
      removeLead(leads.length - 1)
    }
  }

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      addLead(inputValue)
    }
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>

      <div className={`min-h-[42px] p-2 border rounded-lg bg-white dark:bg-gray-800 transition-colors ${inputError
          ? 'border-red-300 dark:border-red-600 focus-within:ring-2 focus-within:ring-red-500 focus-within:border-red-500'
          : 'border-gray-300 dark:border-gray-600 focus-within:ring-2 focus-within:ring-green-500 focus-within:border-green-500'
        }`}>
        <div className="flex flex-wrap gap-2 items-center">
          {/* Lead Chips */}
          {leads.map((lead, index) => (
            <div
              key={`${lead.id}-${index}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 rounded-lg text-sm font-medium border border-green-200 dark:border-green-700 transition-all duration-200 animate-in fade-in slide-in-from-left-2"
            >
              <PhoneIcon className="w-3.5 h-3.5 opacity-70 shrink-0" />
              <div className="flex flex-col">
                <span className="max-w-[150px]  font-medium">{lead.name}</span>
                <span className="max-w-[150px]  text-xs opacity-80">
                  {lead.number || "xxxxxxxxxx"}
                </span>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  removeLead(index)
                }}
                className="ml-0.5 text-red-600 dark:text-red-300 hover:text-red-800 dark:hover:text-red-100 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-full p-1 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 cursor-pointer shrink-0"
                title="Remove this recipient"
                style={{
                  pointerEvents: 'auto',
                  minWidth: '24px',
                  minHeight: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <XMarkIcon className="w-4 h-4" style={{ pointerEvents: 'none' }} />
              </button>
            </div>
          ))}

          {/* Input Field */}
          <input
            type="tel"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value)
              setInputError("")
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleInputBlur}
            placeholder={leads.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
          />
        </div>
      </div>

      {inputError && (
        <p className="text-sm text-red-600 dark:text-red-400">{inputError}</p>
      )}

      {leads.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {leads.length} recipient{leads.length > 1 ? 's' : ''}
        </p>
      )}
    </div>
  )
}

export default LeadChipsInput

